import { useToolOwnership } from "../shared/toolOwnership"
import { useEffect } from "react"
import { getPanelTokenFromUrl } from "../shared/panelUrls"
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react"
import { meetings } from "services/api/meetingsClient"
import type { MeetingResponse } from "services/api/meetingsClient"
import type { JoinInfo, WaitRoomStatus } from "../types"

type UseMeetingWaitingRoomArgs = {
  meetingId?: string
  joinInfo: JoinInfo | null
  meeting: MeetingResponse | null
  setJoinInfo: Dispatch<SetStateAction<JoinInfo | null>>
  setMeeting: Dispatch<SetStateAction<MeetingResponse | null>>
}

/**
 * Waiting-room business rules shared by both renderers: the attendee's admission status
 * and the host "unlock" action that lets future authorized joiners skip knocking.
 * Daily's own knock/admit UI stays inside each surface.
 */
export function useMeetingWaitingRoom({
  meetingId,
  joinInfo,
  meeting,
  setJoinInfo,
  setMeeting,
}: UseMeetingWaitingRoomArgs) {
  const ownership = useToolOwnership(JSON.stringify([meetingId, joinInfo?.native_entry_snapshot?.accessId, joinInfo?.messages_panel?.role, joinInfo?.can_unlock_wait_room]))
  const captureTool = ownership.capture
  const [status, setStatus] = useState<WaitRoomStatus>("idle")
  const [unlocking, setUnlocking] = useState(false)
  const [unlockError, setUnlockError] = useState("")
  const unlockInFlightRef = useRef(false)
  const unlocked = Boolean(joinInfo?.waiting_room_unlocked || meeting?.waiting_room_unlocked)
  const unlockedRef = useRef(unlocked)
  unlockedRef.current = unlocked
  const canUnlock = Boolean(joinInfo?.waiting_room_enabled && joinInfo?.can_unlock_wait_room && !unlocked)

  useEffect(() => { unlockInFlightRef.current = false; setUnlocking(false); setUnlockError('') }, [ownership.key, captureTool])
  const unlock = useCallback(async () => {
    const owns = captureTool()
    if (!meetingId || unlockedRef.current || unlockInFlightRef.current) return false
    unlockInFlightRef.current = true
    setUnlocking(true)
    setUnlockError("")
    try {
      const response = await meetings.unlockWaitRoom(meetingId, joinInfo?.retained_native ? getPanelTokenFromUrl(joinInfo.messages_panel?.url) : undefined)
      if (!owns()) return false
      const nextUnlocked = Boolean(response.waiting_room_unlocked)
      unlockedRef.current = nextUnlocked
      setJoinInfo((prev) => prev ? { ...prev, waiting_room_unlocked: response.waiting_room_unlocked } : prev)
      setMeeting((prev) => prev ? {
        ...prev,
        waiting_room_unlocked: response.waiting_room_unlocked,
        waiting_room_unlocked_at: response.waiting_room_unlocked_at || prev.waiting_room_unlocked_at,
      } : prev)
      return nextUnlocked
    } catch (err: any) {
      if (!owns()) return false
      const message = err?.response?.data?.detail || err?.message || "Unable to unlock the meeting."
      setUnlockError(message)
      return false
    } finally {
      if (owns()) { unlockInFlightRef.current = false; setUnlocking(false) }
    }
  }, [meetingId, setJoinInfo, setMeeting, joinInfo?.retained_native, joinInfo?.messages_panel?.url, captureTool])

  return { status, setStatus, unlocked, unlocking, unlockError, canUnlock, unlock }
}
